import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";

const EASE = [0.16, 1, 0.3, 1];

const DEPARTMENTS = ["ALL", "AI", "ENGINEERING", "DOCTOR"];

const ROLES = [
  { title: "AI Researcher — Clinical Perception", dept: "AI", loc: "Toronto, Canada" },
  { title: "AI Researcher — Vital Signs Modeling", dept: "AI", loc: "Palo Alto, California" },
  { title: "AI Residency", dept: "AI", loc: "Toronto, Canada" },
  { title: "Staff Software Engineer — Applied AI", dept: "AI", loc: "Palo Alto, California" },
  { title: "Senior to Staff AI Product Engineer", dept: "AI", loc: "Phoenix, Arizona" },
  { title: "Simulation Engineer — Patient Flow", dept: "AI", loc: "Phoenix, Arizona" },
  { title: "Camera Hardware Engineer — Optical Array", dept: "ENGINEERING", loc: "Palo Alto, California" },
  { title: "Electrical Engineer — ECG & Sensors", dept: "ENGINEERING", loc: "Phoenix, Arizona" },
  { title: "Mechanical Design Engineer — Hub Enclosure", dept: "ENGINEERING", loc: "Toronto, Canada" },
  { title: "Staff Firmware Engineer — Printer Systems", dept: "ENGINEERING", loc: "Irvine, California" },
  { title: "Senior Motor Control Engineer", dept: "ENGINEERING", loc: "Phoenix, Arizona" },
  { title: "Manufacturing Engineer", dept: "ENGINEERING", loc: "Tucson, Arizona" },
  { title: "Manufacturing Engineer — Assembly", dept: "ENGINEERING", loc: "Fremont, California" },
  { title: "Quality Engineer — Manufacturing", dept: "ENGINEERING", loc: "Fremont, California" },
  { title: "Test Engineer", dept: "ENGINEERING", loc: "Phoenix, Arizona" },
  { title: "Prototype Engineer — 3D Printing", dept: "ENGINEERING", loc: "Toronto, Canada" },
  { title: "Senior Safety Compliance Engineer", dept: "ENGINEERING", loc: "San Diego, California" },
  { title: "Device Management Engineer", dept: "ENGINEERING", loc: "Toronto, Canada" },
  { title: "Senior DevOps Engineer", dept: "ENGINEERING", loc: "Palo Alto, California" },
  { title: "Product Security Engineer", dept: "ENGINEERING", loc: "Phoenix, Arizona" },
  { title: "Senior Frontend Engineer", dept: "ENGINEERING", loc: "Toronto, Canada" },
  { title: "Software Engineer — HL7/FHIR Integrations", dept: "ENGINEERING", loc: "Palo Alto, California" },
  { title: "Physician — Telehealth", dept: "DOCTOR", loc: "Remote, USA" },
  { title: "Physician — Telehealth", dept: "DOCTOR", loc: "Toronto, Canada" },
  { title: "Physician — Telehealth (Part-Time)", dept: "DOCTOR", loc: "Phoenix, Arizona" },
  { title: "Medical Director — Clinical AI", dept: "DOCTOR", loc: "San Diego, California" },
  { title: "Head of Clinical Operations", dept: "DOCTOR", loc: "Toronto, Canada" },
];

export default function Careers() {
  const [dept, setDept] = useState("ALL");
  const [query, setQuery] = useState("");

  const roles = useMemo(
    () =>
      ROLES.filter(
        (r) =>
          (dept === "ALL" || r.dept === dept) &&
          r.title.toLowerCase().includes(query.toLowerCase())
      ),
    [dept, query]
  );

  return (
    <main data-testid="careers-page">
      <Nav />

      <section className="relative h-[70vh] overflow-hidden" data-testid="careers-hero">
        <img
          src="/media/factory.png"
          alt="Inside the SANTELQ factory"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/25" />
        <motion.h1
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.3 }}
          className="absolute bottom-12 left-6 sm:left-12 font-display font-medium tracking-tight text-white text-4xl sm:text-5xl"
          data-testid="careers-title"
        >
          Careers
        </motion.h1>
      </section>

      <section className="py-20 lg:py-28 px-6 sm:px-12 max-w-6xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-12">
          <aside className="flex md:flex-col flex-wrap gap-x-6 gap-y-3" data-testid="careers-departments">
            {DEPARTMENTS.map((d) => (
              <button
                key={d}
                onClick={() => setDept(d)}
                data-testid={`dept-${d.toLowerCase().replace(/[\s&]+/g, "-")}`}
                className={`text-left text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-300 ${
                  dept === d ? "text-[#3A3A3C]" : "text-[#808080] hover:text-[#3A3A3C]"
                }`}
              >
                {d}
              </button>
            ))}
          </aside>

          <div>
            <div className="relative max-w-sm">
              <Search className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-[#808080]" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search roles"
                data-testid="careers-search"
                className="w-full bg-transparent border-b border-black/10 pl-7 py-3 text-sm text-[#3A3A3C] placeholder-[#808080] outline-none focus:border-[#0D0D0E] transition-colors"
              />
            </div>

            <p
              className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#808080] mt-12"
              data-testid="careers-dept-label"
            >
              {dept}
            </p>
            <div className="mt-4" data-testid="careers-roles">
              {roles.length === 0 && (
                <p className="py-10 text-sm text-[#808080]" data-testid="careers-empty">
                  No roles found — try another search.
                </p>
              )}
              {roles.map((r) => (
                <a
                  key={r.title}
                  href="/#pilot"
                  data-testid={`role-${r.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${r.loc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="group flex items-baseline justify-between gap-6 py-5 border-b border-black/5 hover:bg-black/[0.015] transition-colors"
                >
                  <span className="font-display font-medium tracking-tight text-[#3A3A3C] text-lg sm:text-xl group-hover:translate-x-1 transition-transform duration-300">
                    {r.title}
                  </span>
                  <span className="text-xs text-[#808080] whitespace-nowrap">{r.loc}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
